/**
 * shareCard/render — a ShareCard as an SVG, and that SVG as a PNG.
 *
 * The SET is the hero (ruling 10): its name, then every length as a chip in
 * its stroke color, straight from STROKE_COLORS (designTokens.ts) so the card
 * and the Scores tab can never disagree about what a Butterfly looks like.
 * Then the story line, the only thing on the card in the coral accent. The
 * other sets of a swim follow as small strips. Totals come SMALL and LAST,
 * then the club line, then the link line. Brand: the Horizon Break palette
 * and type ramp from web/DESIGN.md (Lexend with the system stack behind it;
 * an SVG drawn into <img> cannot fetch a webfont, so on a device without
 * Lexend the card renders in the system face — see the lane record).
 *
 * Two sizes: "story" 1080×1920 (Instagram Stories, the 9:16 path) and
 * "link" 1200×630 (Open Graph). renderShareCardSvg is pure and synchronous
 * (tests read it as text); renderShareCard rasterizes it through an
 * HTMLImageElement and a 2D canvas and resolves a PNG Blob. In an
 * environment without a 2D canvas (jsdom) it REJECTS with a named error;
 * it never returns a placeholder.
 *
 * A decoded card is untrusted (contract C1): every string is escaped before
 * it reaches the markup and capped in length; a stroke key that is not in
 * STROKE_COLORS draws in the Unknown color, never as raw text.
 */
import { COLORS, contrastTextOn, strokeWord, STROKE_COLORS } from "../designTokens";
import { fmtClockDuration, fmtMeters, fmtPace, pacePer100mSeconds } from "../format";
import { SET_DISPLAY } from "../setLibrary";
import { MAX_HR_LENGTHS, type ShareCard, type ShareSet, type ShareSetBlock, type ShareSwimCard, type StoryLine } from "./types";

export type ShareCardSize = "story" | "link";

export const SHARE_CARD_SIZES: Record<ShareCardSize, { width: number; height: number }> = {
  story: { width: 1080, height: 1920 },
  link: { width: 1200, height: 630 },
};

/** Instagram Stories safe area: the app's own UI covers roughly the top and
 *  bottom 250 px of a 1080×1920 story. Nothing readable is placed there. */
export const STORY_SAFE_TOP = 250;
export const STORY_SAFE_BOTTOM = 250;

const FONT = "Lexend, -apple-system, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";
const CANVAS = COLORS.navyDeep;
const CARD_BG = COLORS.navyMid;
const HORIZON = "#E1C28E"; // accent-tertiary, the first-light glow
const TEXT = COLORS.textPrimary;
const BODY = "#C5C6CE"; // text-body
const MUTED = COLORS.textMuted;
const DIM = COLORS.textDim;
const CORAL = COLORS.coralRed;
const ACCENT = COLORS.swimmerBlue;

/** Chip word fitting: fraction of the chip width a word may span, and the
 *  measured width of one bold capital in ems. Exported for the test. */
export const CHIP_WORD_WIDTH = 0.8;
export const CHIP_GLYPH_EM = 0.66;

/** Longest string the card will draw from any one field. */
const MAX_TEXT = 80;

export function escapeXml(s: string): string {
  return s
    .slice(0, MAX_TEXT)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** The fill for a block: its token, or the Unknown token for a key the palette does not know. */
export function blockFill(stroke: string): string {
  return STROKE_COLORS[stroke] ?? STROKE_COLORS.Unknown;
}

interface TextOpts {
  size: number;
  weight?: number;
  fill?: string;
  anchor?: "start" | "middle" | "end";
  spacing?: number;
  opacity?: number;
  /** A ground-coloured outline painted under the glyphs, so a line passing
   *  behind the text stops short of it instead of striking it through. */
  halo?: string;
}

function text(x: number, y: number, s: string, o: TextOpts): string {
  const attrs = [
    `x="${x}"`,
    `y="${y}"`,
    `font-family="${FONT}"`,
    `font-size="${o.size}"`,
    `font-weight="${o.weight ?? 400}"`,
    `fill="${o.fill ?? TEXT}"`,
    o.anchor ? `text-anchor="${o.anchor}"` : "",
    o.spacing ? `letter-spacing="${o.spacing}"` : "",
    o.opacity !== undefined ? `opacity="${o.opacity}"` : "",
    o.halo ? `stroke="${o.halo}" stroke-width="6" stroke-linejoin="round" paint-order="stroke"` : "",
    `style="font-variant-numeric: tabular-nums"`,
  ]
    .filter(Boolean)
    .join(" ");
  return `<text ${attrs}>${escapeXml(s)}</text>`;
}

/** Estimated glyph width per character, in ems, for wrapping. 0.52 fit the
 *  bold headlines but let a lowercase 24 px line ("Breaststroke · Backstroke")
 *  clip by three glyphs in Chromium's Helvetica; 0.55 wraps that line and
 *  still keeps "Fastest Rockalita yet: 3:52" on one 60 px line. */
export const WRAP_EM = 0.55;
/** Lowercase body text (the blocks line, details) runs wider than the bold
 *  headlines; it wraps on this estimate instead. */
export const BODY_EM = 0.6;

/** Greedy word wrap on an em-per-character estimate (WRAP_EM by default). */
export function wrapLines(s: string, size: number, maxWidth: number, maxLines: number, em: number = WRAP_EM): string[] {
  const perChar = size * em;
  const maxChars = Math.max(8, Math.floor(maxWidth / perChar));
  const words = s.split(/ +/).filter(Boolean); // plain spaces only: a non-breaking space glues a block
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars || !cur) cur = next;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1].slice(0, Math.max(0, maxChars - 1)) + "…";
    return kept;
  }
  return lines;
}

/** One chip per rep, in swim order. */
function lengthsOf(set: ShareSet): ShareSetBlock[] {
  const out: ShareSetBlock[] = [];
  for (const b of set.blocks) for (let i = 0; i < Math.min(b.reps, 60); i++) out.push(b);
  return out;
}

interface ChipGridOpts {
  x: number;
  y: number;
  width: number;
  /** Chip height at one row; the grid shrinks chips to fit the row count. */
  chip: number;
  gap: number;
  /** Max chips per row before wrapping. */
  perRow: number;
  /** Draw the stroke abbreviation inside chips at or above this size. */
  labelAt: number;
  maxRows: number;
  /** Label a single-stroke band ("24 × FREE") even when chips are too small for words. */
  bandLabel?: boolean;
}

/** The word ON a chip: one register, whole words (VP round 1, major 4 — "BK"
 *  read as "back kick"). Unknown keeps the app's "?"; a drill label is itself.
 *  The map lives with the palette (designTokens.strokeWord) so the card's chips
 *  and the Sets tab's stroke tiles can never drift apart. */
export const chipWord = strokeWord;

/** The word in a legend or a block line: the app's full stroke name
 *  ("Freestyle", "Kickboard"); an unknown key reads "Unknown". */
export function legendWord(stroke: string): string {
  return STROKE_COLORS[stroke] ? stroke : "Unknown";
}

/** Strokes drawn anywhere on the card, in order of first appearance. */
export function strokesOn(card: ShareCard): string[] {
  const sets = card.kind === "set" ? [card.set] : card.sets;
  const out: string[] = [];
  for (const s of sets) for (const b of s.blocks) if (!out.includes(b.stroke)) out.push(b.stroke);
  return out;
}

/** The colored sequence: every length a rounded chip in its stroke token. Returns the markup and the height used. */
function chipGrid(set: ShareSet, o: ChipGridOpts): { svg: string; height: number } {
  const chips = lengthsOf(set);
  if (chips.length === 0) return { svg: "", height: 0 };
  // A single-stroke run longer than one row is ONE band with "22 × FREE",
  // like SetGroupRibbon's singleBand: a wall of identical chips reads as a
  // render bug, the band reads as one stroke, N lengths.
  if (chips.length > o.perRow && chips.every((c) => c.stroke === chips[0].stroke)) {
    const fill = blockFill(chips[0].stroke);
    const h = o.chip;
    const label = `${chips.length} × ${chipWord(chips[0].stroke)}`;
    // The band is exactly one full row of chips wide, so it ends where the
    // chip rows beside it end (VP polish item 3: a 500 px band ran past them).
    const pitch = Math.min(o.chip, Math.floor((o.width - o.gap * (o.perRow - 1)) / o.perRow));
    const w = Math.min(o.width, o.perRow * (pitch + o.gap) - o.gap);
    // A strip-sized band (bandLabel) still carries its count: the label takes
    // most of the band's height instead of the hero's 30 %.
    const show = o.bandLabel === true || h >= o.labelAt;
    const size = Math.floor(Math.min(h * (o.bandLabel ? 0.62 : 0.3), (w * CHIP_WORD_WIDTH) / (label.length * CHIP_GLYPH_EM)));
    return {
      svg:
        `<rect x="${o.x}" y="${o.y}" width="${w}" height="${h}" rx="${Math.round(h * 0.18)}" fill="${fill}" stroke="#FFFFFF" stroke-opacity="0.18" stroke-width="2"/>` +
        (show ? text(o.x + w / 2, o.y + h / 2 + size * 0.36, label, { size, weight: 600, fill: contrastTextOn(fill), anchor: "middle" }) : ""),
      height: h,
    };
  }
  const perRow = Math.min(o.perRow, chips.length);
  const size = Math.min(o.chip, Math.floor((o.width - o.gap * (perRow - 1)) / perRow));
  const rows = Math.ceil(chips.length / perRow);
  const shown = Math.min(rows, o.maxRows);
  // One label size for the whole grid: the longest word present must fit
  // inside the chip WITH padding, so BREAST sets the size and FLY takes the
  // same. Bold caps measure ~0.66 em per glyph; the word gets 80 % of the
  // chip, leaving a 10 % pad each side (VP round 2, item 7).
  const longest = Math.max(...chips.map((c) => chipWord(c.stroke).length));
  const labelSize = Math.floor(Math.min(size * 0.3, (size * CHIP_WORD_WIDTH) / (longest * CHIP_GLYPH_EM)));
  const parts: string[] = [];
  for (let i = 0; i < chips.length && Math.floor(i / perRow) < shown; i++) {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const cx = o.x + col * (size + o.gap);
    const cy = o.y + row * (size + o.gap);
    const fill = blockFill(chips[i].stroke);
    parts.push(`<rect x="${cx}" y="${cy}" width="${size}" height="${size}" rx="${Math.round(size * 0.18)}" fill="${fill}" stroke="#FFFFFF" stroke-opacity="0.18" stroke-width="2"/>`);
    if (size >= o.labelAt) {
      parts.push(text(cx + size / 2, cy + size / 2 + labelSize * 0.36, chipWord(chips[i].stroke), { size: labelSize, weight: 600, fill: contrastTextOn(fill), anchor: "middle" }));
    }
  }
  if (rows > shown) {
    const y = o.y + shown * (size + o.gap) + 8;
    parts.push(text(o.x, y + 20, `+${chips.length - shown * perRow} more lengths`, { size: 24, fill: MUTED }));
    return { svg: parts.join(""), height: shown * (size + o.gap) + 40 };
  }
  return { svg: parts.join(""), height: shown * size + (shown - 1) * o.gap };
}

/** "Butterfly · Backstroke · Breaststroke · 2 × Freestyle · …" — the blocks in
 *  the app's own stroke names under the chips. */
function blocksLine(set: ShareSet): string {
  // Non-breaking spaces inside a block: a wrap may fall only at a separator,
  // never between "2 ×" and "Freestyle".
  return set.blocks
    .map((b) => (b.reps === 1 ? legendWord(b.stroke) : `${b.reps}\u00A0×\u00A0${legendWord(b.stroke)}`))
    .join(" · ");
}

/** Swatch + word for every stroke on the card, on one line from x. The strips
 *  draw chips too small for a word, so every colour they use is named here
 *  (VP round 1, major 3: the unlabeled violet Kickboard chip). */
function legend(card: ShareCard, x: number, y: number, size: number, maxWidth: number): { svg: string; height: number } {
  const parts: string[] = [];
  const rowH = size + 16;
  let cx = x;
  let rows = 1;
  for (const stroke of strokesOn(card)) {
    const fill = blockFill(stroke);
    const word = legendWord(stroke);
    const w = size + 8 + Math.round(word.length * size * 0.56);
    if (cx + w > x + maxWidth && cx > x) {
      cx = x;
      y += rowH;
      rows++;
    }
    parts.push(`<rect x="${cx}" y="${y - size * 0.8}" width="${size}" height="${size}" rx="${Math.round(size * 0.22)}" fill="${fill}" stroke="#FFFFFF" stroke-opacity="0.18" stroke-width="1.5"/>`);
    parts.push(text(cx + size + 8, y, word, { size, fill: MUTED }));
    cx += w + 26;
  }
  return { svg: parts.join(""), height: rows * rowH };
}

function fmtPace100(sec: number): string {
  return fmtClockDuration(sec);
}

/** A club-named set (The Rockalita, Killer BS…) by its display name, via the app's own map. */
function isSignatureName(name: string): boolean {
  return Object.values(SET_DISPLAY).some((d) => d.displayName === name && d.isSignature);
}

/** The set the story is about, else the biggest signature set, else the
 *  biggest main-role set, else the biggest set. Ruling 10: a Rockalita is the
 *  hero over a 550 m freestyle set even when the story is about the date. */
export function heroSetOf(card: ShareCard): ShareSet | null {
  if (card.kind === "set") return card.set;
  if (card.sets.length === 0) return null;
  const named = card.sets.find((s) => card.story.text.includes(s.name) || card.story.text.includes(shortName(s.name)));
  if (named) return named;
  const biggest = (xs: ShareSet[]) => xs.reduce((a, b) => (b.totalM > a.totalM ? b : a));
  const signature = card.sets.filter((s) => isSignatureName(s.name));
  if (signature.length > 0) return biggest(signature);
  const main = card.sets.filter((s) => s.blocks.some((b) => b.role === "main"));
  return biggest(main.length > 0 ? main : card.sets);
}

function shortName(name: string): string {
  return name.startsWith("The ") ? name.slice(4) : name;
}

/** "200 m · 8 lengths · 25 m pool · best 1:56 /100" (setTotalsLine) from the
 *  blocks and the lineage. Never a name: the sender is only the byline
 *  (Scott, 12:1x). */
function lengthCount(set: ShareSet): number {
  return set.blocks.reduce((n, b) => n + b.reps, 0);
}

/** A set's heart rate when avg, peak and lengths are all finite, non-negative
 *  numbers. A decoded link can carry anything (the codec checks only types), so
 *  anything else counts as no heart rate and is never drawn (quality review P1). */
export function hrOf(set: ShareSet): ShareSet["hr"] {
  const hr = set.hr;
  if (!hr) return undefined;
  return [hr.avg, hr.peak, hr.lengths].every((n) => typeof n === "number" && Number.isFinite(n) && n >= 0) ? hr : undefined;
}

/** Heart rate on at least half the set's lengths. Under half, a card says
 *  "HR on 1 of 8" in place of numbers (Scott, 09:5x). */
function coversHalf(set: ShareSet): boolean {
  const hr = hrOf(set);
  return !!hr && hr.lengths * 2 >= lengthCount(set);
}

/** Its numbers stand for the set: half its lengths and at least 2 of them,
 *  because one length's reading is not an avg and a peak (orchestrator 10:5x).
 *  The avg · peak words, the Hardest contest and a set card's honesty line all
 *  go by it. */
function showsNumbers(set: ShareSet): boolean {
  return coversHalf(set) && hrOf(set)!.lengths >= 2;
}

function setTotalsLine(set: ShareSet): string {
  const lengths = lengthCount(set);
  const pools = new Set(set.blocks.map((b) => b.distanceM));
  const parts = [fmtMeters(set.totalM), `${lengths} length${lengths === 1 ? "" : "s"}`];
  if (pools.size === 1 && set.blocks.length > 0 && set.blocks[0].distanceM > 0) parts.push(`${set.blocks[0].distanceM} m pool`);
  if (set.lineage?.bestPaceSecPer100) parts.push(`best ${fmtPace100(set.lineage.bestPaceSecPer100)} /100`);
  return parts.join(" · ");
}

/** The name a card's byline credits: the owner's first word, capped, or null. */
export function cardOwner(card: ShareCard): string | null {
  if (!card.owner) return null;
  const first = card.owner.trim().split(/\s+/)[0] ?? "";
  return first ? first.slice(0, 16) : null;
}

/** "Shared by Scott": the one place a card names its sender, small in a set
 *  card's footer and under the Worker's set page story (Scott, 12:1x: "kind of
 *  weird to have your name in it"). A swim card carries none; its footer
 *  already says who swam with whom. */
export function bylineOf(card: ShareCard): string | null {
  const owner = card.kind === "set" ? cardOwner(card) : null;
  return owner ? `Shared by ${owner}` : null;
}

/**
 * The story line as the card draws it: no name and never "you", because the
 * image leaves the phone and is read by someone else. The sender is named only
 * in the byline (bylineOf). A swim card's hero is the whole swim, so its story
 * is framed as the swim's (VP polish, item 2: a set's story under "Share this
 * swim" read as a set share): "New set: first time you swam The Rockalita"
 * draws as "A swim with a first Rockalita". The payload is never rewritten;
 * only the drawing is (and the sheet headline and the text beside the image,
 * which use this).
 */
export function storyForCard(card: ShareCard): StoryLine {
  const text = card.kind === "swim" ? swimStoryText(card.story.text) : theyNotYou(card.story.text);
  return text === card.story.text ? card.story : { ...card.story, text };
}

/** A builder's or a hand-made text never says "you" on a card someone else reads. */
function theyNotYou(text: string): string {
  return text.replace(/\byou\b/g, "they");
}

/** storyLineFor's seven texts (storyLine.ts), framed as the swim's story. */
function swimStoryText(t: string): string {
  let m: RegExpExecArray | null;
  if ((m = /^New set: first time you swam (.+)$/.exec(t))) return `A swim with a first ${shortName(m[1])}`;
  if ((m = /^Fastest (.+) yet: (.+)$/.exec(t))) return `A swim with the fastest ${m[1]} yet: ${m[2]}`;
  if (t === "New sequence") return "A swim in a new sequence";
  if ((m = /^(\d+(?:st|nd|rd|th)) time swimming (.+)$/.exec(t))) return `A swim with the ${m[1]} ${shortName(m[2])}`;
  return theyNotYou(t);
}

/** "53:02 in the water · 2:39 per 100m": the Swim tab hero's own subline
 *  (SwimHeroCard), built from the same two format functions. */
export function swimNumbersLine(card: ShareSwimCard): string {
  const pace = pacePer100mSeconds(card.durationSec, { value: card.distanceM, unit: "m" });
  return `${fmtClockDuration(card.durationSec)} in the water${pace !== null ? ` · ${fmtPace(pace)} per 100m` : ""}`;
}

/** The swim card's footer facts: pool and heart rate. Distance and time are
 *  the hero now (the title and the numbers line), so they are not repeated. */
function totalsLine(card: ShareSwimCard): string | null {
  const parts: string[] = [];
  if (card.poolM > 0) parts.push(`${card.poolM} m pool`);
  // Labelled, so it cannot be read against the app's peak HR (VP round 2, item 3;
  // the app's own word for it is "Avg HR", P4EffortHero). "swim", so it is not
  // read as the set rows' avg, and BPM in the rows' casing (VP nits a + c).
  if (card.avgHr && Number.isFinite(card.avgHr) && card.avgHr > 0) parts.push(`swim avg HR ${card.avgHr} BPM`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** A set's heart rate in the set detail sheet's words (SetDetailSheet:
 *  "142 avg · 168 peak BPM"). */
export function setHrText(hr: NonNullable<ShareSet["hr"]>): string {
  return `${hr.avg} avg · ${hr.peak} peak BPM`;
}

/** A set's heart-rate words on a card: its numbers; "HR on 1 of 8 lengths" in
 *  their place when heart rate covers fewer than half its lengths, because a
 *  few lengths' readings are not the set's effort (Scott, 09:5x). Empty with
 *  none. A set with a single reading over half its lengths uses the same
 *  grammar as every other row: the old "159 BPM · 1 length" form read as a
 *  typo to 5 of 5, and "HR on 1 of 8" without its unit read as 1 of 8 SETS to
 *  4 of 5 (VP round 4, B3 and B4; orchestrator round-5 rulings 3 and 4). */
export function setHrWords(set: ShareSet): string {
  const hr = hrOf(set);
  if (!hr) return "";
  const of = lengthCount(set);
  if (!coversHalf(set)) return `HR on ${hr.lengths} of ${of} length${of === 1 ? "" : "s"}`;
  return setHrText(hr);
}

/** The set with the highest average heart rate, among sets whose heart rate
 *  covers at least 2 lengths and at least half of the set (a 1-length set's
 *  single reading is not a set's effort; VP panel, 10:0x). Null when fewer
 *  than two sets qualify (nothing to compare) or when another qualifying set
 *  ties the top average (no single hardest). */
export function hardestSetOf(sets: ShareSet[]): ShareSet | null {
  return soleTop(sets, (hr) => hr.avg);
}

/** The set whose peak is the swim's highest, among the same sets Hardest
 *  compares (their numbers show). Null with fewer than two of them, or when
 *  another ties the top peak: no single peak (Scott, 12:4x). */
export function peakSetOf(sets: ShareSet[]): ShareSet | null {
  return soleTop(sets, (hr) => hr.peak);
}

/** The one set, among those whose numbers show, with the highest `value`; null
 *  when fewer than two compete or the top is tied. */
function soleTop(sets: ShareSet[], value: (hr: NonNullable<ShareSet["hr"]>) => number): ShareSet | null {
  const withHr = sets.filter(showsNumbers);
  if (withHr.length < 2) return null;
  const top = Math.max(...withHr.map((s) => value(s.hr!)));
  const atTop = withHr.filter((s) => value(s.hr!) === top);
  return atTop.length === 1 ? atTop[0] : null;
}

/** "Heart rate on 56 of 68 lengths" over every length of the given sets, the
 *  set detail sheet's honesty line. Only when some length has none; never on
 *  sets that carry no heart rate at all. */
export function hrCoverageLine(sets: ShareSet[]): string | null {
  if (!sets.some((s) => hrOf(s))) return null;
  const of = sets.reduce((n, s) => n + lengthCount(s), 0);
  const on = sets.reduce((n, s) => n + (hrOf(s)?.lengths ?? 0), 0);
  return on < of ? `Heart rate on ${on} of ${of} lengths` : null;
}

/** A set card's honesty line: its one set's, only beside avg · peak numbers.
 *  When the slot itself says "HR on N of M lengths", the card says the coverage
 *  once (orchestrator rulings, 10:3x and 10:5x). It hangs on coversHalf, not on
 *  showsNumbers: since round-5 ruling 4 a one-reading set's words are plain
 *  numbers that carry no coverage of their own, so this line has to. A swim
 *  card keeps hrCoverageLine: its line sums the whole swim, which no row says. */
export function setCardCoverageLine(set: ShareSet): string | null {
  return coversHalf(set) ? hrCoverageLine([set]) : null;
}

/** The Hardest mark's word and fill (the first-light token). Exported so the
 *  Worker's recipient pages mark the same set with the same word and colour. */
export const HARDEST = "Hardest";
export const HARDEST_FILL = HORIZON;

/** Estimated drawn width of a line, on the wrap estimate. */
function textWidth(s: string, size: number): number {
  return Math.round(s.length * size * WRAP_EM);
}

/** The Hardest mark's word size beside a text line of `lineSize`: a step under it. */
function hardestSize(lineSize: number): number {
  return lineSize - 6;
}

function hardestWidth(lineSize: number): number {
  const size = hardestSize(lineSize);
  return textWidth(HARDEST, size) + size;
}

/** The Hardest mark: chipGrid's rounded rect in the first-light token with
 *  its word inside, from x, centred on the middle of a `lineSize` text line. */
function hardestMark(x: number, baseline: number, lineSize: number): string {
  const size = hardestSize(lineSize);
  const w = hardestWidth(lineSize);
  const h = Math.round(size * 1.5);
  const y = Math.round(baseline - lineSize * 0.36 - h / 2);
  return (
    `<rect x="${Math.round(x)}" y="${y}" width="${w}" height="${h}" rx="${Math.round(h * 0.18)}" fill="${HARDEST_FILL}" stroke="#FFFFFF" stroke-opacity="0.18" stroke-width="2"/>` +
    text(Math.round(x + w / 2), Math.round(y + h / 2 + size * 0.36), HARDEST, { size, weight: 600, fill: contrastTextOn(HARDEST_FILL), anchor: "middle" })
  );
}

/** The Peak HR mark's word. Exported so the Worker's pages mark the same set
 *  with the same word. */
export const PEAK_HR = "Peak HR";

/** What a dim band says in place of a line when its set's row withholds
 *  numbers: the ROW's own fraction, shortened when the band is too narrow for
 *  the whole phrase. Never "no HR" — one reading is not zero, and 5 of 5 read
 *  the band contradicting its row (VP round 5). Exported for the test and so
 *  the Worker's pages can say the same words. */
export function bandHrWords(set: ShareSet, room: number): string {
  const hr = hrOf(set);
  if (!hr) return "";
  const short = `${hr.lengths} of ${lengthCount(set)}`;
  const full = `HR on ${short}`;
  return textWidth(full, 14) <= room ? full : short;
}

function peakWidth(lineSize: number): number {
  const size = hardestSize(lineSize);
  return textWidth(PEAK_HR, size) + size;
}

/** The Peak HR mark: Hardest's shape as a quiet outline, no fill and the
 *  primary text, so Hardest stays the card's one award (orchestrator, 12:4x).
 *  From x, centred on the middle of a `lineSize` text line. */
function peakMark(x: number, baseline: number, lineSize: number): string {
  const size = hardestSize(lineSize);
  const w = peakWidth(lineSize);
  const h = Math.round(size * 1.5);
  const y = Math.round(baseline - lineSize * 0.36 - h / 2);
  return (
    `<rect x="${Math.round(x)}" y="${y}" width="${w}" height="${h}" rx="${Math.round(h * 0.18)}" fill="none" stroke="${TEXT}" stroke-opacity="0.55" stroke-width="1.5"/>` +
    text(Math.round(x + w / 2), Math.round(y + h / 2 + size * 0.36), PEAK_HR, { size, weight: 500, fill: TEXT, anchor: "middle" })
  );
}

/** The set the story line names, if any. Only that row is marked on a
 *  whole-swim card: with no set named, no set is singled out. */
function namedSetOf(card: ShareSwimCard): ShareSet | null {
  return card.sets.find((s) => card.story.text.includes(s.name) || card.story.text.includes(shortName(s.name))) ?? null;
}

/** One size of the whole swim's set rows. "stacked" is the hero size (the name
 *  over a full-width row of chips); "side" is the strip beside the name that
 *  the card drew under THE WHOLE SWIM before (reference still, 05:55). */
interface RowLayout {
  kind: "stacked" | "side";
  nameSize: number;
  /** Width of the name column in the side layout. */
  nameCol: number;
  chip: number;
  gap: number;
  perRow: number;
  maxRows: number;
  rowGap: number;
  /** Size of a set's heart-rate words. */
  hrSize: number;
  /** Heart rate under the name (the story's side strips, where the room is
   *  vertical) or beside it at the row's right edge (the hero rows' full-width
   *  name line, and the link card, where the room is horizontal). */
  hrUnder: boolean;
}

/** Largest first. The first layout whose rows fit the room wins, so a short
 *  swim draws big chips and a long one still draws every set: the stacked
 *  hero size steps down 2 px at a time (40 → 24 px chips, 20+ per row across
 *  the 920 px content width), then the side strips take over. */
export const STORY_ROW_LAYOUTS: RowLayout[] = [
  ...Array.from({ length: 9 }, (_, i): RowLayout => {
    const chip = 40 - 2 * i;
    const gap = chip >= 34 ? 6 : 5;
    const nameSize = Math.max(24, Math.round(chip * 0.8));
    return { kind: "stacked", nameSize, nameCol: 0, chip, gap, perRow: Math.floor((920 + gap) / (chip + gap)), maxRows: 3, rowGap: Math.round(chip * 0.6), hrSize: Math.round(nameSize * 0.8), hrUnder: false };
  }),
  { kind: "side", nameSize: 26, nameCol: 420, chip: 26, gap: 5, perRow: 16, maxRows: 2, rowGap: 16, hrSize: 22, hrUnder: true },
  { kind: "side", nameSize: 24, nameCol: 420, chip: 22, gap: 3, perRow: 80, maxRows: 1, rowGap: 8, hrSize: 20, hrUnder: true },
];
/** With the heart-rate line on, the story's rows may shrink past the sizes
 *  above so the fixed-height chart still fits (Scott 15:38: shrink the rows,
 *  not the chart). One line per set, the words at the row's right edge as on
 *  the link card: an 8-set swim does not fit the two-line strips. Tried only
 *  after every size above, and only with the line on, so a card without it
 *  draws exactly as before. */
export const GRAPH_ROW_LAYOUTS: RowLayout[] = [
  // The name column carries the row's ordinal too (Scott's ruling B), so it is
  // wider than it was: at 380 the ordinal squeezed "Mixed set · 200 m" into an
  // ellipsis on the hardest row, where the Hardest pill also rides.
  { kind: "side", nameSize: 26, nameCol: 400, chip: 24, gap: 4, perRow: 16, maxRows: 1, rowGap: 14, hrSize: 22, hrUnder: false },
  { kind: "side", nameSize: 24, nameCol: 380, chip: 20, gap: 3, perRow: 80, maxRows: 1, rowGap: 8, hrSize: 20, hrUnder: false },
];
const LINK_ROW_LAYOUTS: RowLayout[] = [
  { kind: "side", nameSize: 22, nameCol: 380, chip: 20, gap: 4, perRow: 32, maxRows: 1, rowGap: 10, hrSize: 18, hrUnder: false },
  { kind: "side", nameSize: 20, nameCol: 380, chip: 16, gap: 2, perRow: 80, maxRows: 1, rowGap: 6, hrSize: 16, hrUnder: false },
];

/** The "+N more sets" line: height it takes, drawn only when the smallest layout cannot fit every set. */
const MORE_SETS_H = 44;

/**
 * Every set of the swim as a row, sized to the room between `top` and
 * `maxBottom`. Never drops a set silently: when even the smallest layout
 * cannot fit them all, the rows that fit are drawn and an explicit
 * "+N more sets" line says how many were left out (the Sep 9 card listed
 * only "Warmup · 500 m" for a five-set swim and said nothing).
 */
function setRows(
  sets: ShareSet[],
  named: ShareSet | null,
  o: { x: number; width: number; top: number; maxBottom: number; layouts: RowLayout[]; ordinals?: boolean },
): { svg: string; bottom: number; shown: number } {
  const room = o.maxBottom - o.top;
  const hardest = hardestSetOf(sets);
  const peak = peakSetOf(sets);
  const PEAK_GAP = 10;
  /** A set's heart-rate words: from x ("start"), or ending at x ("end"). On the
   *  swim's peak set the Peak HR mark ends the words, beside the number it names
   *  (Scott 12:4x, orchestrator A): after them from x, or with the words stepped
   *  left so the mark ends at x. */
  const hrWords = (s: ShareSet, l: RowLayout, x: number, baseline: number, anchor: "start" | "end") => {
    if (!hrOf(s)) return "";
    const words = setHrWords(s);
    const style = { size: l.hrSize, fill: MUTED };
    if (s !== peak) return text(x, baseline, words, { ...style, anchor: anchor === "end" ? "end" : undefined });
    if (anchor === "start") return text(x, baseline, words, style) + peakMark(x + textWidth(words, l.hrSize) + PEAK_GAP, baseline, l.hrSize);
    const markX = x - peakWidth(l.hrSize);
    return text(markX - PEAK_GAP, baseline, words, { ...style, anchor: "end" }) + peakMark(markX, baseline, l.hrSize);
  };
  /** The room the beside column takes in this layout: the widest words (with the
   *  Peak HR mark on its set), or none. */
  const besideWidth = (l: RowLayout) => {
    if (l.hrUnder) return 0;
    const widths = sets.filter((s) => hrOf(s)).map((s) => textWidth(setHrWords(s), l.hrSize) + (s === peak ? PEAK_GAP + peakWidth(l.hrSize) : 0));
    return widths.length > 0 ? Math.max(...widths) + 24 : 0;
  };
  /** "Mixed set · 200 m" within `room`, and on the hardest set the Hardest mark
   *  right after the name (VP B2: on the BPM line 4 of 5 panelists missed it).
   *  The name gives up the mark's width, less `slack` (the column's gutter), so
   *  the mark never reaches the chips. */
  const nameLine = (s: ShareSet, l: RowLayout, baseline: number, room: number, slack: number, i: number) => {
    const isNamed = s === named;
    const style = { size: l.nameSize, weight: isNamed ? 600 : 400, fill: isNamed ? ACCENT : BODY };
    const label = `${s.name} · ${fmtMeters(s.totalM)}`;
    // With the heart-rate line on, the row leads with its numeral, the one its
    // band carries in the chart (Scott's ruling B): two rows can read "Mixed
    // set", so only the numeral can say which band is which.
    const ord = o.ordinals ? String(i + 1) : "";
    const ordSize = l.nameSize - 6;
    const ordW = ord ? textWidth(ord, ordSize) + 8 : 0;
    const nx = o.x + ordW;
    const numeral = ord ? text(o.x, baseline, ord, { size: ordSize, fill: MUTED }) : "";
    if (s !== hardest) return numeral + text(nx, baseline, wrapLines(label, l.nameSize, room - ordW, 1)[0], style);
    const gap = 12;
    const shown = wrapLines(label, l.nameSize, room + slack - ordW - gap - hardestWidth(l.nameSize), 1)[0];
    return numeral + text(nx, baseline, shown, style) + hardestMark(nx + textWidth(shown, l.nameSize) + gap, baseline, l.nameSize);
  };
  const measure = (s: ShareSet, l: RowLayout, y: number, beside: number, i: number) => {
    if (l.kind === "stacked") {
      const grid = chipGrid(s, { x: o.x, y: y + l.nameSize + 12, width: o.width, chip: l.chip, gap: l.gap, perRow: l.perRow, labelAt: 999, maxRows: l.maxRows, bandLabel: true });
      const baseline = y + Math.round(l.nameSize * 0.8);
      return { svg: nameLine(s, l, baseline, o.width - beside, 0, i) + hrWords(s, l, o.x + o.width, baseline, "end") + grid.svg, height: l.nameSize + 12 + grid.height };
    }
    const grid = chipGrid(s, { x: o.x + l.nameCol, y, width: o.width - l.nameCol - beside, chip: l.chip, gap: l.gap, perRow: l.perRow, labelAt: 999, maxRows: l.maxRows, bandLabel: true });
    const rowH = Math.max(grid.height, l.nameSize);
    const baseline = y + Math.round(rowH / 2 + l.nameSize * 0.35);
    const name = nameLine(s, l, baseline, l.nameCol - 20, 12, i);
    if (!l.hrUnder || !hrOf(s)) return { svg: name + hrWords(s, l, o.x + o.width, baseline, "end") + grid.svg, height: rowH };
    // Under the name at one line pitch (1.2 em): the two-line cell takes as
    // little height as it can, so heart rate does not push the rows down to
    // smaller chips and lose the "22 × FREE" band (VP nit b).
    const hrBaseline = baseline + Math.round(l.hrSize * 1.2);
    return { svg: name + hrWords(s, l, o.x, hrBaseline, "start") + grid.svg, height: Math.max(rowH, hrBaseline - y + Math.round(l.hrSize * 0.2)) };
  };
  const draw = (l: RowLayout, limit: number) => {
    const parts: string[] = [];
    const beside = besideWidth(l);
    let y = o.top;
    let shown = 0;
    for (const [i, s] of sets.entries()) {
      const row = measure(s, l, y, beside, i);
      if (y + row.height > o.top + limit) break;
      parts.push(row.svg);
      y += row.height + l.rowGap;
      shown++;
    }
    return { parts, bottom: shown > 0 ? y - l.rowGap : o.top, shown };
  };
  for (const l of o.layouts) {
    const d = draw(l, room);
    if (d.shown === sets.length) return { svg: d.parts.join(""), bottom: d.bottom, shown: d.shown };
  }
  const smallest = o.layouts[o.layouts.length - 1];
  const d = draw(smallest, room - MORE_SETS_H);
  const left = sets.length - d.shown;
  d.parts.push(text(o.x, d.bottom + 34, `+${left} more set${left === 1 ? "" : "s"}`, { size: 26, fill: MUTED }));
  return { svg: d.parts.join(""), bottom: d.bottom + MORE_SETS_H, shown: d.shown };
}

// ── the swim's heart-rate line (Scott 2026-09-11 15:38, mock variant B) ──────

/** The peak callout's red: its dot and its label, nothing else on the card. Not CORAL, which stays the story line's alone. */
export const PEAK_RED = "#FF4D5E";

/** The swim story card's heart-rate block at 1080 wide: header and plot. The
 *  same at any set count; the rows shrink to make room for it
 *  (GRAPH_ROW_LAYOUTS). Exported for the test. */
export const HR_GRAPH_H = 208;

/** The story card's margin and content width: the card draws to them, and the
 *  graph's fit is tested against them before the card budgets its room. */
const STORY_M = 80;
const STORY_CW = SHARE_CARD_SIZES.story.width - 2 * STORY_M;

function isBpm(b: unknown): b is number {
  return typeof b === "number" && Number.isFinite(b) && b > 0 && b <= 300;
}

/** A set's per-length readings as the line draws them: one per length, anything
 *  but a plausible BPM as none. Null when it carries no readings, or an array
 *  that does not match its lengths (a decoded link can carry anything). */
export function perLengthOf(set: ShareSet): (number | null)[] | null {
  const raw = hrOf(set)?.perLength;
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_HR_LENGTHS || raw.length !== lengthCount(set)) return null;
  return raw.map((b) => (isBpm(b) ? b : null));
}

/** The swim story card draws the line when its sets hold two readings or more
 *  AND their bands fit the card's content width, so the room the card budgets
 *  for the block is the room the block actually takes. */
export function hasHrGraph(card: ShareCard): boolean {
  if (card.kind !== "swim") return false;
  const readings = card.sets.reduce((n, s) => n + (perLengthOf(s)?.filter((b) => b !== null).length ?? 0), 0);
  return readings >= 2 && graphSpans(card.sets, STORY_M, STORY_CW) !== null;
}

/** Span widths that give every set at least `need` and share the rest by
 *  `weight` (its lengths). Null when the needs alone overflow `avail`. */
function fillWidths(need: number[], weight: number[], avail: number): number[] | null {
  if (need.reduce((a, b) => a + b, 0) > avail) return null;
  const fixed = need.map(() => false);
  for (;;) {
    const free = avail - need.reduce((n, w, i) => (fixed[i] ? n + w : n), 0);
    const total = weight.reduce((n, w, i) => (fixed[i] ? n : n + w), 0);
    const pitch = total > 0 ? free / total : 0;
    const short = weight.map((w, i) => !fixed[i] && w * pitch < need[i]);
    if (!short.includes(true)) return weight.map((w, i) => (fixed[i] ? need[i] : w * pitch));
    short.forEach((s, i) => (fixed[i] ||= s));
  }
}

interface GraphSpan {
  set: ShareSet;
  x0: number;
  x1: number;
}

/** Band minimums, widest first: a band carries its ordinal and its peak number,
 *  and the rest between sets takes the gap. A long swim takes a tighter pair. */
const GRAPH_BANDS = [
  { min: 44, rest: 30 },
  { min: 44, rest: 20 },
  { min: 34, rest: 14 },
  { min: 22, rest: 8 },
];

/**
 * Each set's span across the chart, in swim order: at least `min` wide, the
 * rest shared out by lengths, with a gap for the rest between sets.
 *
 * The chart carries NO set names. Two bands can both read "Mixed set", so a
 * name on the band could not say which row the peak belonged to (VP round 3,
 * 4 of 5); and staggered names, with or without a leader, were read as missing
 * (5 of 5). Scott's ruling B: a numeral at each band's corner, the same numeral
 * before the set's name in the rows. Null when even the tightest band does not fit.
 */
function graphSpans(sets: ShareSet[], x: number, width: number): GraphSpan[] | null {
  const weight = sets.map((s) => Math.max(1, lengthCount(s)));
  for (const band of GRAPH_BANDS) {
    const widths = fillWidths(sets.map(() => band.min), weight, width - band.rest * (sets.length - 1));
    if (!widths) continue;
    const spans: GraphSpan[] = [];
    let x0 = x;
    for (const [i, set] of sets.entries()) {
      spans.push({ set, x0, x1: x0 + widths[i] });
      x0 += widths[i] + band.rest;
    }
    return spans;
  }
  return null;
}

/** A label of half-width `half` centred on `cx`, moved just inside [x, x+width];
 *  one wider than the room is centred in it. Exported for the test. */
export function centreIn(cx: number, half: number, x: number, width: number): number {
  if (2 * half >= width) return Math.round(x + width / 2);
  const at = Math.min(Math.max(cx, x + half), x + width - half);
  // Whole pixels, and the rounding itself stays inside: rounding a clamped
  // label outward put its edge 0.4 px past the card.
  return Math.min(Math.max(Math.round(at), Math.ceil(x + half)), Math.floor(x + width - half));
}

const pt = (n: number) => n.toFixed(1);
const pathOf = (pts: [number, number][]) => pts.map(([px, py], i) => `${i ? "L" : "M"}${pt(px)} ${pt(py)}`).join(" ");

/**
 * The line's BPM range, from the lengths that carry the story (orchestrator
 * ruling, 16:0x): June 11's lone 89 BPM warmup reading had squashed every set
 * into the plot's top third. Only sets with 2 or more readings count; a set
 * with fewer still draws its band and its dot, at the edge if it falls
 * outside. Floor: the lower of the 10th percentile (nearest rank) of their
 * readings and their lowest average less 8 BPM. Ceiling: their highest peak
 * plus 6 BPM, so the red peak dot and its label sit clear of the frame's edge. With no such set,
 * every reading counts. Null with no readings. Exported for the test.
 */
export function hrGraphDomain(sets: ShareSet[]): { floor: number; ceiling: number } | null {
  const all = sets.map((s) => ({ hr: hrOf(s), readings: (perLengthOf(s) ?? []).filter(isBpm) })).filter((x) => x.readings.length > 0);
  const story = all.filter((x) => x.readings.length >= 2);
  const pool = story.length > 0 ? story : all;
  const values = pool.flatMap((x) => x.readings).sort((a, b) => a - b);
  if (values.length === 0) return null;
  const p10 = values[Math.max(0, Math.ceil(values.length * 0.1) - 1)];
  const avgs = pool.map((x) => x.hr!.avg).filter(isBpm);
  const peaks = pool.map((x) => x.hr!.peak).filter(isBpm);
  return {
    floor: Math.min(p10, ...avgs.map((a) => a - 8)),
    ceiling: Math.max(values[values.length - 1], ...peaks) + 6,
  };
}

/** The index of a set's highest reading (the last, on a tie: heart rate lags the effort), or -1. */
function topReading(readings: (number | null)[]): number {
  let at = -1;
  readings.forEach((b, i) => {
    if (b !== null && (at < 0 || b >= readings[at]!)) at = i;
  });
  return at;
}

/**
 * The swim's heart rate as one line, per length, under the set rows: each set's
 * span shaded and numbered (the numeral its row carries), the dip in the rest
 * between sets dashed, every band's peak printed, and the swim's peak called
 * out in red: a dot and "Peak 171",
 * no level line (Scott 16:0x). The red label says hr.peak, the highest SAMPLE of the set Peak HR
 * marks (peakSetOf), never a per-length average; its dot sits at that level
 * over the set's highest length. Draws in HR_GRAPH_H from `top`.
 */
function hrGraph(sets: ShareSet[], named: ShareSet | null, x: number, width: number, top: number): string {
  const spans = graphSpans(sets, x, width);
  if (!spans) return "";
  const headY = top + 44;
  const plotTop = headY + 22;
  // No name row under the plot (the ordinals key the bands), so the plot takes
  // the room those rows used to: 44 + 22 + 130 + 12 = HR_GRAPH_H.
  const plotH = 130;
  const plotBottom = plotTop + plotH;
  const readings = sets.map((s) => perLengthOf(s) ?? []);
  const rests = sets.map((s) => (isBpm(hrOf(s)?.restAfter) ? (hrOf(s)!.restAfter as number) : null));
  const peakSet = peakSetOf(sets);
  const peakIndex = peakSet ? sets.indexOf(peakSet) : -1;
  const peak = peakIndex >= 0 && isBpm(peakSet!.hr!.peak) && topReading(readings[peakIndex]) >= 0 ? peakSet!.hr!.peak : null;
  const domain = hrGraphDomain(sets);
  if (!domain) return "";
  const { floor, ceiling } = domain;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 6;
  // A reading outside the range draws at its edge, never off the plot.
  const yOf = (b: number) => plotTop + PAD_TOP + ((ceiling - Math.min(Math.max(b, floor), ceiling)) / (ceiling - floor)) * (plotH - PAD_TOP - PAD_BOTTOM);
  const points = spans.map((sp, i) => {
    const pitch = (sp.x1 - sp.x0) / Math.max(1, readings[i].length);
    return readings[i].map((b, j): [number, number] | null => (b === null ? null : [sp.x0 + (j + 0.5) * pitch, yOf(b)]));
  });

  const back: string[] = [
    text(x, headY, "HEART RATE", { size: 24, weight: 600, fill: MUTED, spacing: 5 }),
    `<defs><linearGradient id="hr-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${ACCENT}" stop-opacity="0.26"/><stop offset="1" stop-color="${ACCENT}" stop-opacity="0"/></linearGradient></defs>`,
  ];
  const line: string[] = [];
  const marks: string[] = [];
  const ordinals: { x0: number; x1: number; y0: number; y1: number }[] = [];
  // The chart mirrors the ROW's own stats rule (round 5): a set whose row
  // withholds avg · peak, because heart rate covers under half its lengths,
  // draws no line, no dot and no number here either. A lone dot pinned to the
  // floor read as a broken render to 5 of 5 (VP round 4); the band stays,
  // dimmer, and echoes its ROW's own fraction: "no HR" beside a row reading
  // "HR on 1 of 8 lengths" was read as a contradiction by 5 of 5 (VP round 5).
  const shows = spans.map((sp) => coversHalf(sp.set));
  // What each band actually draws. A set whose row DOES show numbers but has
  // exactly one reading holds that value flat across its whole band, its mark
  // on the middle of the segment: one held value, never an empty band.
  const drawn: ([number, number] | null)[][] = points.map((ps, i) => (shows[i] ? ps : []));
  for (const [i, sp] of spans.entries()) {
    const only = drawn[i].filter((p): p is [number, number] => p !== null);
    if (shows[i] && only.length === 1) drawn[i] = [[Math.round((sp.x0 + sp.x1) / 2), only[0][1]]];
  }

  for (const [i, sp] of spans.entries()) {
    const isNamed = sp.set === named;
    const dim = !shows[i] ? 0.02 : isNamed ? 0.075 : 0.045;
    back.push(`<rect x="${pt(sp.x0)}" y="${plotTop - 6}" width="${pt(sp.x1 - sp.x0)}" height="${plotH + 12}" rx="6" fill="#FFFFFF" opacity="${dim}"/>`);
    // The band's ordinal, in its corner: the row of the same numeral names it.
    // It is the only key from a band back to its row, so nothing may cover it;
    // its box is kept for the red callout to dodge (VP round 4).
    const ord = String(i + 1);
    const ordX = Math.round(sp.x0 + 8);
    back.push(text(ordX, plotTop + 14, ord, { size: 16, weight: 500, fill: MUTED }));
    ordinals.push({ x0: ordX, x1: ordX + textWidth(ord, 16), y0: plotTop - 2, y1: plotTop + 18 });
    if (!shows[i]) {
      const word = bandHrWords(sp.set, sp.x1 - sp.x0 - 8);
      const half = textWidth(word, 14) / 2;
      back.push(text(centreIn((sp.x0 + sp.x1) / 2, half, sp.x0, sp.x1 - sp.x0), plotTop + 36, word, { size: 14, fill: MUTED, anchor: "middle" }));
      continue;
    }
    const only = drawn[i].filter((p): p is [number, number] => p !== null);
    if (only.length === 1) {
      // One reading, held flat across the band (round 5, ruling 2).
      const [, fy] = only[0];
      const x0 = sp.x0 + 6;
      const x1 = sp.x1 - 6;
      back.push(`<path d="M${pt(x0)} ${pt(fy)} L${pt(x1)} ${pt(fy)} L${pt(x1)} ${plotBottom} L${pt(x0)} ${plotBottom} Z" fill="url(#hr-area)"/>`);
      line.push(`<path d="M${pt(x0)} ${pt(fy)} L${pt(x1)} ${pt(fy)}" fill="none" stroke="${ACCENT}" stroke-width="2.5" stroke-linecap="round"/>`);
      continue;
    }
    // Runs of lengths with a reading: a length without one breaks the line, as in the app's chart.
    let run: [number, number][] = [];
    const flush = () => {
      if (run.length > 1) {
        back.push(`<path d="${pathOf(run)} L${pt(run[run.length - 1][0])} ${plotBottom} L${pt(run[0][0])} ${plotBottom} Z" fill="url(#hr-area)"/>`);
        line.push(`<path d="${pathOf(run)}" fill="none" stroke="${ACCENT}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>`);
      }
      run = [];
    };
    for (const p of drawn[i]) {
      if (p) {
        run.push(p);
        line.push(`<circle cx="${pt(p[0])}" cy="${pt(p[1])}" r="2.4" fill="${ACCENT}"/>`);
      } else flush();
    }
    flush();
  }

  // The rest between two sets: the last length drawn, the rest's BPM midway,
  // the next DRAWN length. A band that draws nothing is bridged across.
  for (const [i] of spans.entries()) {
    const mine = drawn[i].filter((p): p is [number, number] => p !== null);
    const last = mine[mine.length - 1];
    if (!last || rests[i] === null) continue;
    const next = drawn.slice(i + 1).flat().find((p): p is [number, number] => p !== null);
    if (!next) continue;
    line.push(`<path d="${pathOf([last, [(last[0] + next[0]) / 2, yOf(rests[i]!)], next])}" fill="none" stroke="${ACCENT}" stroke-width="2" stroke-dasharray="4 5" opacity="0.5"/>`);
  }

  // Marks, the red callout first so a set's number never covers it.
  const boxes: { x0: number; x1: number; y0: number; y1: number }[] = [];
  const clear = (b: (typeof boxes)[number]) => boxes.every((o) => b.x1 < o.x0 || b.x0 > o.x1 || b.y1 < o.y0 || b.y0 > o.y1);
  // The dot and its label draw over everything. No level line (Scott 16:0x: "I like just the red "Peak" with the number").
  let redMark = "";
  if (peak !== null) {
    const [px] = drawn[peakIndex][topReading(readings[peakIndex])]!;
    const py = yOf(peak);
    // Over the dot, not beside it: right-anchored, the label sat two bands left
    // of the set it names and 4 of 5 readers pinned the peak there (VP, 16:2x).
    const word = `Peak ${peak}`;
    const half = textWidth(word, 22) / 2;
    let lx = centreIn(px, half, x, width);
    // Above the dot, unless that sits on a band's ordinal: the peak band's own
    // numeral was under this label at 8 sets (VP round 4). The LABEL moves, the
    // numeral never does — below its dot where the plot has the room, else
    // stepped right of the numerals it was covering.
    const onOrdinal = (cx: number, top: number, bottom: number) =>
      ordinals.filter((o) => !(cx + half < o.x0 || cx - half > o.x1 || bottom < o.y0 || top > o.y1));
    let ly = Math.round(py - 16);
    const hit = onOrdinal(lx, ly - 22, ly + 6);
    if (hit.length > 0) {
      const below = Math.round(py + 34);
      if (below + 6 <= plotBottom + 6) ly = below;
      else lx = centreIn(Math.max(...hit.map((o) => o.x1)) + half + 8, half, x, width);
    }
    redMark =
      `<circle cx="${pt(px)}" cy="${pt(py)}" r="8" fill="${PEAK_RED}" stroke="${CANVAS}" stroke-width="2.5"/>` +
      text(lx, ly, word, { size: 22, weight: 600, fill: PEAK_RED, anchor: "middle", halo: CARD_BG });
    boxes.push({ x0: lx - half, x1: lx + half, y0: ly - 22, y1: ly + 6 }, { x0: px - 9, x1: px + 9, y0: py - 9, y1: py + 9 });
  }
  for (const [i, sp] of spans.entries()) {
    if (i === peakIndex && peak !== null) continue;
    // Every band whose ROW shows numbers carries its peak number, the
    // one-reading sets included: 5 of 5 panelists read the only band without a
    // number as no data at all (VP round 3). A band whose row withholds them
    // draws nothing at all and said so above (round 5, ruling 1).
    if (!shows[i] || !isBpm(hrOf(sp.set)?.peak)) continue;
    const only = drawn[i].filter((p): p is [number, number] => p !== null);
    const flat = only.length === 1;
    const at = flat ? 0 : topReading(readings[i]);
    if (at < 0) continue;
    const [px, py] = (flat ? only[0] : drawn[i][at])!;
    marks.push(`<circle cx="${pt(px)}" cy="${pt(py)}" r="4.5" fill="${TEXT}" stroke="${CANVAS}" stroke-width="2"/>`);
    const word = String(hrOf(sp.set)!.peak);
    const half = textWidth(word, 18) / 2;
    // A held value's number stays inside its OWN band: centred on the chart it
    // drifted over the next band and was read as that band's (VP round 4, B2).
    const cx = flat ? centreIn(px, half, sp.x0, sp.x1 - sp.x0) : centreIn(px, half, x, width);
    const box = { x0: cx - half, x1: cx + half, y0: py - 24, y1: py - 6 };
    if (!clear(box)) continue;
    boxes.push(box);
    marks.push(text(cx, Math.round(py - 10), word, { size: 18, weight: 500, fill: BODY, anchor: "middle", halo: CARD_BG }));
  }
  return back.join("") + line.join("") + marks.join("") + redMark;
}

function clubLine(card: ShareCard): string | null {
  if (card.kind === "set") return bylineOf(card);
  const mates = card.clubmates ?? [];
  if (mates.length === 0) return null;
  if (mates.length <= 3) return `with ${mates.join(", ")}`;
  return `${mates.length} in the club this week`;
}

const LINK = "swimsetclub.com";

/** "Jun 11, 2026" from the card's "YYYY-MM-DD"; anything else is shown as given. */
function cardDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** The sky, the glow, and (when `ruleY` is given) the thin horizon rule at that y. */
function backdrop(w: number, h: number, ruleY: number | null): string {
  return (
    `<defs>` +
    `<linearGradient id="sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${CANVAS}"/><stop offset="0.62" stop-color="${CARD_BG}"/><stop offset="1" stop-color="${CANVAS}"/></linearGradient>` +
    `<radialGradient id="glow" cx="0.5" cy="0.66" r="0.6"><stop offset="0" stop-color="${HORIZON}" stop-opacity="0.16"/><stop offset="1" stop-color="${HORIZON}" stop-opacity="0"/></radialGradient>` +
    `</defs>` +
    `<rect width="${w}" height="${h}" fill="url(#sky)"/>` +
    `<rect width="${w}" height="${h}" fill="url(#glow)"/>` +
    (ruleY === null ? "" : horizonRule(w, ruleY))
  );
}

function horizonRule(w: number, y: number): string {
  return `<rect x="0" y="${Math.round(y)}" width="${w}" height="2" fill="${HORIZON}" opacity="0.25"/>`;
}

/** The story card's footer, pinned above the bottom chrome: date, totals,
 *  club, link on a swim card; club and link on a set card. Returns the markup
 *  and its highest baseline, so the content above can be fitted to it. */
function storyFooter(card: ShareCard, M: number, H: number, lift = 0): { svg: string; topLine: number } {
  const parts: string[] = [];
  let by = H - STORY_SAFE_BOTTOM - 30 - lift;
  let topLine = by; // the footer's highest drawn baseline
  parts.push(text(M, by, LINK, { size: 30, fill: DIM }));
  by -= 52;
  const club = clubLine(card);
  if (club) {
    // A set card's slot is its byline, small (Scott 12:1x); a swim card's is its clubmates.
    parts.push(text(M, by, club, card.kind === "set" ? { size: 28, fill: MUTED } : { size: 32, fill: ACCENT }));
    topLine = by;
    by -= 50;
  }
  if (card.kind === "swim") {
    const totals = totalsLine(card);
    if (totals) {
      // With heart rate the line is as bright as the club line, the footer's
      // strongest, and no brighter (orchestrator ruling, 10:3x).
      parts.push(text(M, by, totals, { size: 32, fill: card.avgHr ? ACCENT : MUTED }));
      by -= 50;
    }
    parts.push(text(M, by, cardDate(card.date), { size: 28, fill: DIM }));
    topLine = by;
  }
  return { svg: parts.join(""), topLine };
}

/** A swim card's date baseline below the horizon rule: 23 px from the divider
 *  to the date's glyph tops, as on the reference card. Exported for the test. */
export const SWIM_DATE_UNDER_RULE = 44;

/** Story lines and detail lines as the card wraps them. */
/** A story's closing number rides with the word before it (a non-breaking
 *  space), so a wrap never leaves "3:52" or "24.7s" alone on a coral line. */
export function glueTail(s: string): string {
  return s.replace(/ (\S*\d\S*)$/, "\u00A0$1");
}

function storyLines(card: ShareCard, CW: number): { story: string[]; detail: string[] } {
  const story = storyForCard(card);
  return {
    story: wrapLines(glueTail(story.text), 60, CW, 3),
    detail: story.detail ? wrapLines(story.detail, 36, CW, 2) : [],
  };
}

function storySvg(card: ShareCard): string {
  const { width: W, height: H } = SHARE_CARD_SIZES.story;
  const M = STORY_M;
  const CW = STORY_CW;
  // A swim card's hero is the WHOLE swim (Scott, 2026-09-10 05:56); a set
  // card's hero is its set (ruling 10).
  const hero = card.kind === "set" ? card.set : null;
  // The horizon rule is placed after the content is measured (VP round 2,
  // item 4: a fixed 0.75 H cut through the last strip row).
  const parts: string[] = [backdrop(W, H, null)];
  // Instagram Stories draws its own chrome over the top and bottom ~250 px of
  // a 1080×1920 image; every line of this card starts below STORY_SAFE_TOP and
  // the footer ends above H - STORY_SAFE_BOTTOM (see the safe-area still).
  let y = STORY_SAFE_TOP + 40;
  const footer = storyFooter(card, M, H);
  const lines = storyLines(card, CW);

  parts.push(text(M, y, "SWIM SET CLUB", { size: 30, weight: 600, fill: MUTED, spacing: 8 }));
  y += 96;

  // The whole swim: its numbers as the Swim tab's hero shows them, then every
  // set as a row in its stroke colours, sized so all of them fit above the
  // story line.
  if (card.kind === "swim") {
    parts.push(text(M, y, fmtMeters(card.distanceM), { size: 84, weight: 600 }));
    y += 58;
    parts.push(text(M, y, swimNumbersLine(card), { size: 30, fill: MUTED }));
    y += 46;
    if (card.sets.length > 0) {
      y += 24;
      parts.push(text(M, y, "THE WHOLE SWIM", { size: 24, weight: 600, fill: MUTED, spacing: 5 }));
      // The honesty line rides the header, over the rows whose heart rate it qualifies.
      const coverage = hrCoverageLine(card.sets);
      if (coverage) parts.push(text(W - M, y, coverage, { size: 24, fill: MUTED, anchor: "end" }));
      y += 38;
      const key = legend(card, M, y, 22, CW);
      parts.push(key.svg);
      y += key.height + 4;
      // What the story, its detail, the rule and the footer need below the rows,
      // as drawn: the story's first baseline sits 106 under the last chip (54 +
      // 52), each story line takes 72, each detail line 46, then the rule 16
      // under the content and the date SWIM_DATE_UNDER_RULE under the rule. (It
      // was budgeted 12 px short, which only showed once rows filled the room.)
      // With the heart-rate line, its fixed block sits between the rows and the story.
      const graph = hasHrGraph(card);
      const below = 106 + lines.story.length * 72 + lines.detail.length * 46 + 16 + SWIM_DATE_UNDER_RULE + (graph ? HR_GRAPH_H : 0);
      const named = namedSetOf(card);
      const layouts = graph ? [...STORY_ROW_LAYOUTS, ...GRAPH_ROW_LAYOUTS] : STORY_ROW_LAYOUTS;
      const rows = setRows(card.sets, named, { x: M, width: CW, top: y, maxBottom: footer.topLine - below, layouts, ordinals: graph });
      parts.push(rows.svg);
      y = rows.bottom;
      if (graph) {
        parts.push(hrGraph(card.sets, named, M, CW, y));
        y += HR_GRAPH_H;
      }
      y += 54;
    }
  }

  if (hero) {
    const nameLines = wrapLines(hero.name, 84, CW, 2);
    for (const l of nameLines) {
      parts.push(text(M, y, l, { size: 84, weight: 600 }));
      y += 96;
    }
    y += 24;
    const grid = chipGrid(hero, { x: M, y, width: CW, chip: 110, gap: 14, perRow: 8, labelAt: 56, maxRows: 6 });
    parts.push(grid.svg);
    y += grid.height + 56;
    // Full stroke names can run long (round 2 item 6): wrap to two lines.
    for (const l of wrapLines(blocksLine(hero), 26, CW, 3, BODY_EM)) {
      parts.push(text(M, y, l, { size: 26, fill: BODY }));
      y += 34;
    }
    y += 8;
    // A set card's story line IS its lineage, so the metres stand alone here,
    // with the set's heart rate and its honesty line when it has one.
    parts.push(text(M, y, [fmtMeters(hero.totalM), setHrWords(hero)].filter(Boolean).join(" · "), { size: 30, fill: MUTED }));
    y += 46;
    const coverage = setCardCoverageLine(hero);
    if (coverage) {
      parts.push(text(M, y - 6, coverage, { size: 26, fill: MUTED }));
      y += 40;
    }
  }

  // The story line: coral, the only coral on the card.
  y += 52;
  for (const l of lines.story) {
    parts.push(text(M, y, l, { size: 60, weight: 600, fill: CORAL }));
    y += 72;
  }
  for (const l of lines.detail) {
    parts.push(text(M, y, l, { size: 36, fill: BODY }));
    y += 46;
  }

  // A set card fills its middle with the set's own whiteboard: one row per
  // block, its swatch, its words, its pace (VP round 1, blocker 2).
  if (card.kind === "set" && hero) {
    y += 60;
    parts.push(text(M, y, "THE SET", { size: 24, weight: 600, fill: MUTED, spacing: 5 }));
    y += 48;
    // Every block gets a row, or the card says how many it left out: the
    // pitch shrinks to fit the space above the totals line (min 44 px), and
    // past that an explicit "+N more" row replaces silent truncation.
    const roomBottom = H - STORY_SAFE_BOTTOM - 150; // totals line + rule + gap
    const rows = hero.blocks.length;
    const pitch = Math.max(44, Math.min(54, Math.floor((roomBottom - y) / rows)));
    const fits = Math.floor((roomBottom - y) / pitch);
    const shown = rows <= fits ? rows : Math.max(1, fits - 1);
    for (const b of hero.blocks.slice(0, shown)) {
      const fill = blockFill(b.stroke);
      parts.push(`<rect x="${M}" y="${y - 30}" width="40" height="40" rx="8" fill="${fill}" stroke="#FFFFFF" stroke-opacity="0.18" stroke-width="2"/>`);
      parts.push(text(M + 60, y, b.label, { size: 34, fill: TEXT }));
      if (b.paceSecPer100) parts.push(text(W - M, y, `${fmtPace100(b.paceSecPer100)} /100`, { size: 28, fill: MUTED, anchor: "end" }));
      y += pitch;
    }
    if (shown < rows) {
      parts.push(text(M + 60, y, `+${rows - shown} more blocks`, { size: 30, fill: MUTED }));
      y += pitch;
    }
    // The set's totals under the whiteboard (VP round 2, item 1). The payload
    // carries no date or set time (ShareSetCard, frozen C1); see Needs Scott.
    // The times-swum sentence is the story line; only the best pace rides here.
    y += 12;
    parts.push(text(M, y, setTotalsLine(hero), { size: 32, fill: MUTED }));
  }

  const contentBottom = y;

  // Totals small and last, then club, then link, all above the bottom chrome.
  // The rule sits just under the content. A swim card's footer follows the
  // rule at the reference card's spacing instead of pinning to the bottom and
  // leaving a band: Scott's 05:55 phone card measures 23 px from the divider
  // to the TOP of the date glyphs (orchestrator, verified round), which is a
  // date baseline 44 under the rule at this 28 px size (32 measured 11 px).
  // The fit above was measured against the pinned footer, so lifting it never
  // overlaps. A set card keeps its pinned footer and draws the rule only when
  // there is a clear gap.
  const ruleY = contentBottom + 16;
  if (card.kind === "swim") {
    const lifted = storyFooter(card, M, H, Math.max(0, footer.topLine - (ruleY + SWIM_DATE_UNDER_RULE)));
    parts.push(lifted.svg, horizonRule(W, ruleY));
  } else {
    parts.push(footer.svg);
    if (ruleY <= footer.topLine - 38) parts.push(horizonRule(W, ruleY));
  }
  return wrap(W, H, parts);
}

function linkSvg(card: ShareCard): string {
  const { width: W, height: H } = SHARE_CARD_SIZES.link;
  const M = 56;
  const CW = W - 2 * M;
  const hero = card.kind === "set" ? card.set : null;
  // Content is drawn from y = 70, measured, then centred in the space above
  // the footer (VP round 1, minor 5: a dead band sat above the pinned footer).
  const parts: string[] = [];
  const by = H - 44;
  const footerTop = by - 40;
  const ruleY = footerTop - 8;
  const story = storyForCard(card);
  const storyText = wrapLines(glueTail(story.text), 40, CW, 2);
  let y = 70;
  parts.push(text(M, y, "SWIM SET CLUB", { size: 22, weight: 600, fill: MUTED, spacing: 6 }));
  y += 64;
  // The whole swim: its numbers, then every set as a strip that fits.
  if (card.kind === "swim") {
    parts.push(text(M, y, fmtMeters(card.distanceM), { size: 56, weight: 600 }));
    y += 36;
    parts.push(text(M, y, swimNumbersLine(card), { size: 24, fill: MUTED }));
    // The honesty line, right-aligned over the heart-rate column of the rows.
    const coverage = hrCoverageLine(card.sets);
    if (coverage) parts.push(text(W - M, y, coverage, { size: 20, fill: MUTED, anchor: "end" }));
    y += 22;
    if (card.sets.length > 0) {
      // Below the rows: 50 to the first story baseline, 48 a story line, the
      // detail line (or 26 back when there is none), 10 of clearance; all of it
      // above the rule's limit (ruleY - 14, below), so the rows never force the
      // shrink path.
      const below = 50 + storyText.length * 48 + (story.detail ? 0 : -26) + 10;
      const rows = setRows(card.sets, namedSetOf(card), { x: M, width: CW, top: y, maxBottom: ruleY - 14 - below, layouts: LINK_ROW_LAYOUTS });
      parts.push(rows.svg);
      y = rows.bottom;
    }
    y += 50;
  }
  if (hero) {
    parts.push(text(M, y, wrapLines(hero.name, 56, CW, 1)[0], { size: 56, weight: 600 }));
    y += 32;
    const grid = chipGrid(hero, { x: M, y, width: CW, chip: 96, gap: 10, perRow: 10, labelAt: 48, maxRows: 2 });
    parts.push(grid.svg);
    y += grid.height + 40;
    for (const l of wrapLines([blocksLine(hero), fmtMeters(hero.totalM)].join("  ·  "), 24, CW, 2, BODY_EM)) {
      parts.push(text(M, y, l, { size: 24, fill: BODY }));
      y += 32;
    }
    if (hrOf(hero)) {
      parts.push(text(M, y, [setHrWords(hero), setCardCoverageLine(hero)].filter(Boolean).join("  ·  "), { size: 24, fill: MUTED }));
      y += 32;
    }
    y += 24;
  }
  for (const l of storyText) {
    parts.push(text(M, y, l, { size: 40, weight: 600, fill: CORAL }));
    y += 48;
  }
  if (story.detail) {
    parts.push(text(M, y, wrapLines(story.detail, 26, CW, 1)[0], { size: 26, fill: BODY }));
  } else {
    y -= 26; // no detail line: the block ends at the story baseline
  }
  const contentBottom = y + 10;
  // The last content line must end above the horizon rule. A tall set (two chip
  // rows, a two-line stroke legend, a story with its detail) used to run past it
  // and draw "beat 1:41 from Aug 2" ON the rule (lane open-set, VP panel): the
  // centring offset was clamped at 0 and nothing else gave. Fits: centre as
  // before, never below the limit. Does not fit: shrink the block from its top.
  const limit = ruleY - 14;
  const contentTop = 70 - 22; // cap top of the kicker line
  const fits = contentBottom <= limit;
  const dy = fits ? Math.max(0, Math.min(Math.floor((footerTop - contentBottom) / 2), limit - contentBottom)) : 0;
  const scale = fits ? 1 : (limit - contentTop) / (contentBottom - contentTop);
  const transform = fits
    ? `translate(0 ${dy})`
    : `translate(${M} ${contentTop}) scale(${scale.toFixed(4)}) translate(${-M} ${-contentTop})`;
  const foot: string[] = [];
  if (card.kind === "swim") {
    foot.push(cardDate(card.date));
    const totals = totalsLine(card);
    if (totals) foot.push(totals);
  }
  const club = clubLine(card);
  if (club) foot.push(club);
  const page = [
    backdrop(W, H, ruleY),
    `<g transform="${transform}">${parts.join("")}</g>`,
    text(M, by, foot.join("  ·  "), { size: 22, fill: MUTED }),
    text(W - M, by, LINK, { size: 22, fill: DIM, anchor: "end" }),
  ];
  return wrap(W, H, page);
}

function wrap(w: number, h: number, parts: string[]): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${parts.join("")}</svg>`;
}

/** The SVG as an image source the app's CSP allows (`img-src 'self' data:`). */
export function svgDataUrl(svg: string): string {
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

/** Pure: the card as SVG markup at the given size. */
export function renderShareCardSvg(card: ShareCard, size: ShareCardSize): string {
  return size === "story" ? storySvg(card) : linkSvg(card);
}

/**
 * The card as a PNG Blob (C4). Draws the SVG into a 2D canvas via an
 * HTMLImageElement; the PNG is exactly SHARE_CARD_SIZES[size]. Rejects with
 * "share card: …" when the environment has no 2D canvas, cannot load the SVG,
 * or cannot encode a PNG. Never substitutes a placeholder.
 */
export async function renderShareCard(card: ShareCard, size: ShareCardSize): Promise<Blob> {
  const { width, height } = SHARE_CARD_SIZES[size];
  if (typeof document === "undefined") throw new Error("share card: no document to rasterize in");
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("share card: 2D canvas unavailable");

  // A data: URL, not a blob: URL. The app's CSP (web/index.html) is
  // `img-src 'self' data:`; a blob: image is refused under it (measured in
  // Chrome, 2026-09-09 23:03) and no lane may edit index.html. data: keeps
  // the card independent of that call.
  const img = new Image(width, height);
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("share card: SVG failed to load as an image"));
    img.src = svgDataUrl(renderShareCardSvg(card, size));
  });
  // WebKit can fire onload for an SVG image before it is decodable, and a
  // drawImage at that moment paints nothing. decode() waits for the pixels;
  // where it is missing or rejects after a successful load, onload stands.
  if (typeof img.decode === "function") await img.decode().catch(() => undefined);
  ctx.drawImage(img, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("share card: PNG encode returned null"))), "image/png");
  });
}
